import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Filter, X, Search, ShoppingBag } from 'lucide-react';
import { useCart } from '../App';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', name: 'All Drops' },
  { id: 'hoodies', name: 'Hoodies' },
  { id: 'tshirts', name: 'Tees' },
  { id: 'crewnecks', name: 'Crewnecks' },
  { id: 'jackets', name: 'Outerwear' },
  { id: 'hats', name: 'Hats' },
  { id: 'accessories', name: 'Accessories' },
];

const Shop = () => {
  useEffect(() => { document.title = 'Shop | Crookid Smilez'; }, []);
  const { category: urlCategory } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(urlCategory || 'all');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSizes, setSelectedSizes] = useState({});

  useEffect(() => {
    if (urlCategory) setSelectedCategory(urlCategory);
  }, [urlCategory]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = selectedCategory === 'all' ? `${API}/products` : `${API}/products?category=${selectedCategory}`;
        const res = await fetch(url);
        setProducts(await res.json());
      } catch (error) {
        // Error fetching products
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setMobileFilterOpen(false);
    setSearchParams(cat === 'all' ? {} : { category: cat });
  };

  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleQuickAdd = (product) => {
    const size = selectedSizes[product.id] || product.sizes[0];
    addToCart({
      product_id: product.id, name: product.name, price: product.price,
      size, color: product.colors[0], quantity: 1, image: product.images[0]
    });
    toast.success(`${product.name} added`);
  };

  return (
    <div className="fade-in" data-testid="shop-page">
      {/* Header */}
      <section className="bg-cs-dark border-b border-cs-border py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-2">Catalog</p>
          <h1 className="font-anton text-4xl md:text-5xl uppercase text-cs-text tracking-wider" data-testid="shop-title">
            Shop All
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-56 flex-shrink-0" data-testid="desktop-filters">
            <h2 className="font-mono text-xs uppercase tracking-widest text-cs-ochre mb-4">Categories</h2>
            <nav className="space-y-1">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`block w-full text-left font-mono text-xs uppercase tracking-wider py-2 px-3 border transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-cs-olive text-cs-text border-cs-olive'
                      : 'border-transparent text-cs-muted hover:text-cs-ochre hover:border-cs-border'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </nav>
          </aside>

          <div className="flex-1">
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cs-muted" />
                <input
                  type="text" placeholder="Search products..."
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 bg-cs-card border border-cs-border text-cs-text font-mono text-sm focus:outline-none focus:border-cs-ochre transition-colors placeholder:text-cs-muted/50"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-cs-muted hover:text-cs-text" data-testid="clear-search">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Filter */}
            <div className="md:hidden mb-4">
              <button onClick={() => setMobileFilterOpen(true)} className="w-full border border-cs-border text-cs-muted font-mono text-xs uppercase tracking-wider py-3 flex items-center justify-center gap-2 hover:border-cs-ochre transition-colors" data-testid="mobile-filter-btn">
                <Filter className="w-4 h-4" /> Filter
              </button>
            </div>

            {/* Active Filter */}
            {selectedCategory !== 'all' && (
              <div className="mb-4 flex items-center gap-2">
                <span className="inline-flex items-center gap-2 bg-cs-olive text-cs-text font-mono text-xs uppercase tracking-wider px-3 py-1.5">
                  {categories.find(c => c.id === selectedCategory)?.name}
                  <button onClick={() => handleCategoryChange('all')} data-testid="clear-filter"><X className="w-3 h-3" /></button>
                </span>
              </div>
            )}

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="bg-cs-card border border-cs-border h-[480px] animate-pulse" />
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-20">
                <p className="font-mono text-sm text-cs-muted">{searchQuery ? `No products found for "${searchQuery}"` : 'No products in this category.'}</p>
                <button onClick={() => { setSearchQuery(''); handleCategoryChange('all'); }} className="mt-4 border border-cs-border text-cs-muted font-mono text-xs uppercase tracking-wider px-6 py-2 hover:border-cs-ochre hover:text-cs-ochre transition-colors" data-testid="view-all-btn">
                  View All
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="bg-cs-card border border-cs-border hover:border-cs-olive2 transition-all duration-300 group flex flex-col" data-testid={`product-${product.id}`}>
                    <Link to={`/product/${product.id}`} className="relative aspect-[3/4] overflow-hidden bg-cs-dark">
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <span className="absolute top-3 left-3 bg-cs-olive text-cs-text font-mono text-xs px-2.5 py-1 tracking-wider uppercase">{product.category}</span>
                      {product.shopify_url && (
                        <span className="absolute top-3 right-3 bg-cs-ochre text-cs-dark font-mono text-xs px-2 py-0.5 tracking-wider uppercase">Shopify</span>
                      )}
                    </Link>
                    <div className="p-4 border-t border-dashed border-cs-ochre/30 flex-1 flex flex-col">
                      <Link to={`/product/${product.id}`}>
                        <h3 className="font-bold text-cs-text text-sm uppercase tracking-wide mb-1">{product.name}</h3>
                      </Link>
                      <p className="font-mono text-cs-ochre text-lg mb-3">${product.price.toFixed(2)}</p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {product.sizes.slice(0, 5).map(size => (
                          <button
                            key={`size-${size}-${product.id}`}
                            onClick={() => setSelectedSizes(prev => ({ ...prev, [product.id]: size }))}
                            className={`font-mono text-xs px-2.5 py-1 border transition-colors ${
                              (selectedSizes[product.id] || product.sizes[0]) === size
                                ? 'bg-cs-ochre text-cs-dark border-cs-ochre'
                                : 'border-cs-border text-cs-muted hover:border-cs-olive2'
                            }`}
                            data-testid={`size-chip-${size}-${product.id}`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => handleQuickAdd(product)}
                        className="mt-auto w-full bg-cs-dark border border-cs-border text-cs-text font-mono uppercase tracking-wider text-xs py-2.5 hover:bg-cs-olive hover:border-cs-olive transition-colors flex items-center justify-center gap-2"
                        data-testid={`quick-add-button-${product.id}`}
                      >
                        <ShoppingBag className="w-3.5 h-3.5" /> Quick Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileFilterOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-cs-bg2 border-t border-cs-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-cs-ochre">Categories</h2>
              <button onClick={() => setMobileFilterOpen(false)} className="p-2 border border-cs-border text-cs-muted" data-testid="close-filter-drawer"><X className="w-4 h-4" /></button>
            </div>
            <nav className="space-y-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`block w-full text-left font-mono text-sm uppercase tracking-wider py-3 px-4 border transition-colors ${
                    selectedCategory === cat.id ? 'bg-cs-olive text-cs-text border-cs-olive' : 'border-cs-border text-cs-muted hover:border-cs-ochre'
                  }`}
                  data-testid={`mobile-filter-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
